// ptoService.js
// Service functions for managing PTO balances in Firestore

import { db } from '../firebase/config.js';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  query, 
  where,
  Timestamp,
  writeBatch
} from 'firebase/firestore';

/**
 * Get current PTO year (July 1 - June 30)
 * @returns {string} PTO year in format "2024-2025"
 */
export const getCurrentPTOYear = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  
  if (today.getMonth() >= 6) {
    // July or later: current year to next year
    return `${currentYear}-${currentYear + 1}`;
  } else {
    // Before July: previous year to current year
    return `${currentYear - 1}-${currentYear}`;
  }
};

/**
 * Calculate PTO year start month (0-indexed: 6 = July)
 * @returns {number} Month number (0-11)
 */
export const getPTOYearStartMonth = () => {
  return 6; // July
};

/**
 * Calculate PTO year start day
 * @returns {number} Day of month
 */
export const getPTOYearStartDay = () => {
  return 1; // First day of month
};

/**
 * Initialize PTO balance for a single employee
 * @param {string} employeeId - Firebase UID of employee
 * @param {string} tenantId - Tenant ID
 * @param {number} defaultDays - Default annual PTO days from settings
 * @param {string} ptoYear - Optional PTO year, defaults to current
 * @returns {Promise<void>}
 */
export const initializePTOBalance = async (employeeId, tenantId, defaultDays, ptoYear = null) => {
  try {
    const year = ptoYear || getCurrentPTOYear();
    const docId = `${employeeId}_${year}`;
    
    const balanceRef = doc(db, 'employee_pto_balances', docId);
    
    // Check if already exists
    const existingDoc = await getDoc(balanceRef);
    if (existingDoc.exists()) {
      console.log(`PTO balance already exists for ${employeeId} - ${year}`);
      return;
    }
    
    // Create new balance
    await setDoc(balanceRef, {
      employeeId,
      tenantId,
      ptoYear: year,
      daysAllocated: defaultDays,
      daysUsed: 0,
      daysPending: 0,
      lastUpdated: Timestamp.now(),
      notes: '',
      createdAt: Timestamp.now(),
      createdBy: 'system'
    });
    
    console.log(`Created PTO balance for ${employeeId} - ${year}: ${defaultDays} days`);
  } catch (error) {
    console.error('Error initializing PTO balance:', error);
    throw error;
  }
};

/**
 * Bulk initialize PTO balances for all employees in a tenant
 * @param {string} tenantId - Tenant ID
 * @param {number} defaultDays - Default annual PTO days from settings
 * @param {string} ptoYear - Optional PTO year, defaults to current
 * @returns {Promise<Object>} Summary of initialization
 */
export const bulkInitializePTOBalances = async (tenantId, defaultDays, ptoYear = null) => {
  try {
    const year = ptoYear || getCurrentPTOYear();
    
    // Get all users (employees) for this tenant
    // Assuming you have a roleId field: 2 = employee, 1 = admin, etc.
    const usersQuery = query(
  collection(db, 'faculty_staff'),  // ← CORRECT
  where('tenantId', '==', tenantId)
);
    
    const usersSnapshot = await getDocs(usersQuery);
    
    const batch = writeBatch(db);
    let created = 0;
    let skipped = 0;
    
    for (const userDoc of usersSnapshot.docs) {
  const userData = userDoc.data();
  console.log('User doc:', userDoc.id, 'userData:', userData);
  
  const employeeId = userDoc.id;
  
  if (!employeeId) {
    console.log('⚠️ No uid found, skipping user:', userDoc.id);
    skipped++;
    continue;
  }
  const docId = `${employeeId}_${year}`;
  const balanceRef = doc(db, 'employee_pto_balances', docId);
  
  console.log(`Checking ${docId}...`);
  
  // Check if balance already exists
  const existingBalance = await getDoc(balanceRef);
  
  console.log(`  exists? ${existingBalance.exists()}`);
  
  if (existingBalance.exists()) {
    skipped++;
    console.log(`  ⏭️ Skipping (already exists)`);
    continue;
  }
  
  console.log(`  ✅ Adding to batch`);
  
  // Add to batch
  batch.set(balanceRef, {
    employeeId,
    tenantId,
    ptoYear: year,
    daysAllocated: defaultDays,
    daysUsed: 0,
    daysPending: 0,
    lastUpdated: Timestamp.now(),
    notes: '',
    createdAt: Timestamp.now(),
    createdBy: 'bulk_init'
  });
  
  created++;
}
    
// Commit batch
if (created > 0) {
  console.log(`Attempting to commit ${created} new PTO balances...`);
  try {
    await batch.commit();
    console.log('✅ Batch commit successful!');
  } catch (batchError) {
    console.error('❌ Batch commit FAILED:', batchError);
    throw batchError;
  }
} else {
  console.log('No new balances to create (all skipped)');
}
    
    return {
      success: true,
      created,
      skipped,
      total: usersSnapshot.size,
      message: `Created ${created} PTO balances, skipped ${skipped} existing`
    };
    
  } catch (error) {
    console.error('Error bulk initializing PTO balances:', error);
    throw error;
  }
};

/**
 * Get employee's PTO balance for current year
 * @param {string} employeeId - Firebase UID
 * @param {string} tenantId - Tenant ID
 * @returns {Promise<Object|null>} Balance data or null
 */
export const getEmployeePTOBalance = async (employeeId, tenantId) => {
  try {
    const ptoYear = getCurrentPTOYear();
    const docId = `${employeeId}_${ptoYear}`;
    const balanceRef = doc(db, 'employee_pto_balances', docId);
    
    const balanceDoc = await getDoc(balanceRef);
    
    if (!balanceDoc.exists()) {
      return null;
    }
    
    return {
      id: balanceDoc.id,
      ...balanceDoc.data()
    };
  } catch (error) {
    console.error('Error getting PTO balance:', error);
    throw error;
  }
};

/**
 * Update employee's PTO balance
 * @param {string} employeeId - Firebase UID
 * @param {string} tenantId - Tenant ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateEmployeePTOBalance = async (employeeId, tenantId, updates) => {
  try {
    const ptoYear = getCurrentPTOYear();
    const docId = `${employeeId}_${ptoYear}`;
    const balanceRef = doc(db, 'employee_pto_balances', docId);
    
    await updateDoc(balanceRef, {
      ...updates,
      lastUpdated: Timestamp.now()
    });
    
    console.log(`Updated PTO balance for ${employeeId}`);
  } catch (error) {
    console.error('Error updating PTO balance:', error);
    throw error;
  }
};

/**
 * Deduct PTO days when absence is approved
 * @param {string} employeeId - Firebase UID
 * @param {string} tenantId - Tenant ID
 * @param {number} days - Number of days to deduct
 * @returns {Promise<void>}
 */
export const deductPTODays = async (employeeId, tenantId, days) => {
  try {
    const balance = await getEmployeePTOBalance(employeeId, tenantId);
    
    if (!balance) {
      throw new Error('No PTO balance found for employee');
    }
    
    const newDaysUsed = balance.daysUsed + days;
    
    await updateEmployeePTOBalance(employeeId, tenantId, {
      daysUsed: newDaysUsed
    });
    
    console.log(`Deducted ${days} PTO days from ${employeeId}. New total: ${newDaysUsed}`);
  } catch (error) {
    console.error('Error deducting PTO days:', error);
    throw error;
  }
};

/**
 * Add days to pending when absence request is submitted
 * @param {string} employeeId - Firebase UID
 * @param {string} tenantId - Tenant ID
 * @param {number} days - Number of days pending
 * @returns {Promise<void>}
 */
export const addPendingPTODays = async (employeeId, tenantId, days) => {
  try {
    const balance = await getEmployeePTOBalance(employeeId, tenantId);
    
    if (!balance) {
      throw new Error('No PTO balance found for employee');
    }
    
    const newDaysPending = balance.daysPending + days;
    
    await updateEmployeePTOBalance(employeeId, tenantId, {
      daysPending: newDaysPending
    });
    
    console.log(`Added ${days} pending PTO days for ${employeeId}`);
  } catch (error) {
    console.error('Error adding pending PTO days:', error);
    throw error;
  }
};

/**
 * Remove days from pending and optionally add to used (when request is approved/denied)
 * @param {string} employeeId - Firebase UID
 * @param {string} tenantId - Tenant ID
 * @param {number} days - Number of days to remove from pending
 * @param {boolean} approve - If true, add to daysUsed; if false, just remove from pending
 * @returns {Promise<void>}
 */
export const resolvePendingPTODays = async (employeeId, tenantId, days, approve = false) => {
  try {
    const balance = await getEmployeePTOBalance(employeeId, tenantId);
    
    if (!balance) {
      throw new Error('No PTO balance found for employee');
    }
    
    const updates = {
      daysPending: Math.max(0, balance.daysPending - days)
    };
    
    if (approve) {
      updates.daysUsed = balance.daysUsed + days;
    }
    
    await updateEmployeePTOBalance(employeeId, tenantId, updates);
    
    console.log(`Resolved ${days} pending PTO days for ${employeeId}. Approved: ${approve}`);
  } catch (error) {
    console.error('Error resolving pending PTO days:', error);
    throw error;
  }
};

/**
 * Get all employees' PTO balances for a tenant (for admin views)
 * @param {string} tenantId - Tenant ID
 * @param {string} ptoYear - Optional PTO year, defaults to current
 * @returns {Promise<Array>} Array of balance objects
 */
export const getAllEmployeePTOBalances = async (tenantId, ptoYear = null) => {
  try {
    const year = ptoYear || getCurrentPTOYear();
    
    const balancesQuery = query(
      collection(db, 'employee_pto_balances'),
      where('tenantId', '==', tenantId),
      where('ptoYear', '==', year)
    );
    
    const snapshot = await getDocs(balancesQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all PTO balances:', error);
    throw error;
  }
};

/**
 * Update PTO balance when absence request status changes
 * @param {string} employeeId - Firebase Auth UID of the employee
 * @param {string} tenantId - Tenant ID
 * @param {number} days - Number of days in the absence request
 * @param {string} oldStatus - Previous status (null, 'pending', 'approved', 'denied', 'cancelled')
 * @param {string} newStatus - New status
 * @param {string} ptoYear - Optional PTO year, defaults to current
 * @returns {Promise<Object>} Result object with success status
 */
export const updatePTOForAbsenceChange = async (
  employeeId, 
  tenantId, 
  days, 
  oldStatus, 
  newStatus, 
  ptoYear = null
) => {
  try {
    const year = ptoYear || getCurrentPTOYear();
    
    // First, find the employee's faculty_staff document by uid
    const facultyQuery = query(
      collection(db, 'faculty_staff'),
      where('uid', '==', employeeId),
      where('tenantId', '==', tenantId)
    );
    
    const facultySnapshot = await getDocs(facultyQuery);
    
    if (facultySnapshot.empty) {
      console.log('No faculty_staff found for employee:', employeeId);
      return { success: false, error: 'Employee not found' };
    }
    
    const facultyDocId = facultySnapshot.docs[0].id;
    const balanceId = `${facultyDocId}_${year}`;
    const balanceRef = doc(db, 'employee_pto_balances', balanceId);
    
    // Get current balance
    const balanceDoc = await getDoc(balanceRef);
    
    if (!balanceDoc.exists()) {
      console.log('No PTO balance found for:', balanceId);
      return { success: false, error: 'PTO balance not found' };
    }
    
    const currentBalance = balanceDoc.data();
    let updates = {
      lastUpdated: Timestamp.now()
    };
    
    // Calculate changes based on status transitions
    // Old status: null/pending → New status: approved
    if ((oldStatus === null || oldStatus === 'pending') && newStatus === 'approved') {
      updates.daysPending = Math.max(0, (currentBalance.daysPending || 0) - days);
      updates.daysUsed = (currentBalance.daysUsed || 0) + days;
    }
    // Old status: null → New status: pending (new request)
    else if (oldStatus === null && newStatus === 'pending') {
      updates.daysPending = (currentBalance.daysPending || 0) + days;
    }
    // Old status: pending → New status: denied
    else if (oldStatus === 'pending' && newStatus === 'denied') {
      updates.daysPending = Math.max(0, (currentBalance.daysPending || 0) - days);
    }
    // Old status: approved → New status: cancelled
    else if (oldStatus === 'approved' && newStatus === 'cancelled') {
      updates.daysUsed = Math.max(0, (currentBalance.daysUsed || 0) - days);
    }
    // Old status: pending → New status: cancelled
    else if (oldStatus === 'pending' && newStatus === 'cancelled') {
      updates.daysPending = Math.max(0, (currentBalance.daysPending || 0) - days);
    }
    
    // Update the balance
    await updateDoc(balanceRef, updates);
    
    console.log(`PTO updated for ${balanceId}:`, updates);
    
    return { success: true, updates };
  } catch (error) {
    console.error('Error updating PTO balance:', error);
    return { success: false, error: error.message };
  }
};